import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  AwarenessContentRow,
  AwarenessSectionType,
  createAwarenessSection,
  deleteAwarenessSection,
  getAwarenessSections,
  updateAwarenessSection,
} from '../../../services/Writer/awarenessService';
import { AwarenessEditorBlock } from './AwarenessEditorBlock';


const SECTION_OPTIONS: AwarenessSectionType[] = ['what', 'why', 'prevention', 'faq', 'summary'];



const AwarenessSectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { event_id } = useParams<{ event_id: string }>();

  const [rows, setRows] = useState<AwarenessContentRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [newSection, setNewSection] = useState<AwarenessSectionType>('what');
  const [newContent, setNewContent] = useState('');
  const [newOrder, setNewOrder] = useState(1);
  const [newStatus, setNewStatus] = useState<'published' | 'draft'>('published');
  const [eventType, setEventType] = useState<'health' | 'seasonal'>('health');

  const load = async () => {
    if (!event_id) {
      setRows([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getAwarenessSections(event_id);
      setRows(data);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event_id]);

  const addRow = async () => {
    if (!event_id) {
      toast.error('Missing event id');
      return;
    }

    if (!newContent.trim()) {
      toast.error('Content is required');
      return;
    }

    try {
      await createAwarenessSection({
        event_id,
        event_type: eventType,
        section: newSection,
        content: newContent.trim(),
        display_order: newOrder,
        status: newStatus,
      });
      toast.success('Section created');
      setNewContent('');
      setNewOrder(1);
      setNewStatus('published');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Create failed');
    }
  };

  const saveRow = async (row: AwarenessContentRow) => {
    try {
      await updateAwarenessSection(row.id, {
        section: row.section,
        content: row.content,
        display_order: row.display_order,
        status: row.status,
      });
      toast.success('Updated');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Update failed');
    }
  };

  const removeRow = async (sectionId: number) => {
    if (!window.confirm('Delete this section?')) return;

    try {
      await deleteAwarenessSection(sectionId);
      setRows((prev) => prev.filter((x) => x.id !== sectionId));
      toast.success('Deleted');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <ToastContainer position="top-right" autoClose={2500} />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Awareness Sections</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage what/why/prevention/faq/summary for this event.</p>
        </div>
        <button
          onClick={() => navigate('/writer/awareness')}
          className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700"
        >
          Back
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">Add New Section</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value as 'health' | 'seasonal')}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
          >
            <option value="health">Health</option>
            <option value="seasonal">Seasonal</option>
          </select>

          <select
            value={newSection}
            onChange={(e) => setNewSection(e.target.value as AwarenessSectionType)}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
          >
            {SECTION_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <input
            type="number"
            min={1}
            value={newOrder}
            onChange={(e) => setNewOrder(Number(e.target.value || 1))}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
            placeholder="Display order"
          />

          <select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value as 'published' | 'draft')}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
          >
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>

          <button onClick={addRow} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white">
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        <div className="mt-3">
          <AwarenessEditorBlock
            label="Section Content"
            value={newContent}
            onChange={setNewContent}
            placeholder="Section content"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-600 dark:text-gray-400">Loading...</div>
      ) : (
        <div className="space-y-4">
          {rows.map((row, idx) => (
            <div key={row.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                <select
                  value={row.section}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((x, i) => (i === idx ? { ...x, section: e.target.value as AwarenessSectionType } : x))
                    )
                  }
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                >
                  {SECTION_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>

                <input
                  type="number"
                  value={row.display_order}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((x, i) => (i === idx ? { ...x, display_order: Number(e.target.value || 1) } : x))
                    )
                  }
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                />

                <select
                  value={row.status}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((x, i) => (i === idx ? { ...x, status: e.target.value as 'published' | 'draft' } : x))
                    )
                  }
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>

                <div className="flex justify-end gap-2">
                  <button onClick={() => saveRow(row)} className="px-3 py-2 rounded bg-blue-600 text-white">
                    Save
                  </button>
                  <button onClick={() => removeRow(row.id)} className="px-3 py-2 rounded bg-red-600 text-white">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-3">
                <AwarenessEditorBlock
                  label="Content"
                  value={row.content}
                  onChange={(newVal) => {
                    setRows((prev) => prev.map((x, i) => (i === idx ? { ...x, content: newVal } : x)));
                  }}
                  placeholder="Edit section content..."
                />
              </div>
            </div>
          ))}

          {rows.length === 0 && (
            <div className="text-center text-gray-500 py-10">No sections found.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AwarenessSectionsPage;